import React, { useState, useEffect } from 'react';
import Card, { CardBody, CardHeader } from '../../../components/bootstrap/Card';
import Button from '../../../components/bootstrap/Button';
import Input from '../../../components/bootstrap/forms/Input';
import Select from '../../../components/bootstrap/forms/Select';
import Spinner from '../../../components/bootstrap/Spinner';
import Badge from '../../../components/bootstrap/Badge';
import PaginationButtons from '../../../components/PaginationButtons';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { format } from 'date-fns';
import { apiClient } from '../../../shared/base-classes/apiBaseClass';
import ascendingIcon from '../../../sorting/ascending-sorting.png';
import descendingIcon from '../../../sorting/descending-sorting.png';

interface CountryData {
	ip: string;
	requestCount: number;
	country?: string;
	countryCode?: string;
	region?: string;
	city?: string;
}

interface CountryApiResponse {
	data: {
		data: CountryData[];
		statistics: {
			totalRequests: number;
			uniqueIPs: number;
			avgRequestsPerIP: number;
			completionRate: number;
		};
		per_page: number;
		total_records: number;
		current_page: number;
		next_page: number | null;
		previous_page: number | null;
		period: {
			start: string;
			end: string;
		};
	};
	errors: any[];
	message: string;
	status: boolean;
}

const CountryReport = () => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { t } = useTranslation();
	const [data, setData] = useState<CountryData[]>([]);
	const [loading, setLoading] = useState(false);
	const [sortField, setSortField] = useState<string>('requestCount');
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
	const [statistics, setStatistics] = useState({
		totalRequests: 0,
		uniqueIPs: 0,
		avgRequestsPerIP: 0,
		completionRate: 0
	});
	const [pagination, setPagination] = useState({
		current_page: 1,
		total_records: 0,
		per_page: 10,
		next_page: null as number | null,
		previous_page: null as number | null
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [filters, setFilters] = useState({
		search: '',
		startDate: '',
		endDate: '',
		per_page: 10
	});

	const fetchData = async () => {
		setLoading(true);
		try {
		const params = {
			per_page: perPage,
			page: currentPage,
			search: filters.search,
			startDate: filters.startDate,
			endDate: filters.endDate,
			order_by_field: sortField,
			order_by: sortOrder
		};

			const result: CountryApiResponse = await apiClient.get('admin/analytics/ip-analytics', { params });

			if (result.status) {
				setData(result.data.data);
				setStatistics(result.data.statistics);
				setPagination({
					current_page: result.data.current_page,
					total_records: result.data.total_records,
					per_page: result.data.per_page,
					next_page: result.data.next_page,
					previous_page: result.data.previous_page
				});
			}
		} catch (error) {
			console.error('Error fetching country data:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage, perPage, filters, sortField, sortOrder]);

	const handleFilterChange = (field: string, value: any) => {
		setFilters(prev => ({ ...prev, [field]: value }));
		setCurrentPage(1);
		// Update perPage state when per_page filter changes
		if (field === 'per_page') {
			setPerPage(value);
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const toggleSortOrder = (field: string) => {
		if (sortField === field) {
			setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortField(field);
			setSortOrder('desc');
		}
	};

	const handleClearFilters = () => {
		setFilters({
			search: '',
			startDate: '',
			endDate: '',
			per_page: 10
		});
		setCurrentPage(1);
	};

	return (
		<div>
			<Card>
				<CardHeader>
					<h5>IP Analytics Report</h5>
					<p className="text-muted">Top IP addresses with request counts and geographic information</p>
				</CardHeader>
				<CardBody className="table-responsive">
					{/* Filters */}
					<div className="row mb-4">
						<div className="col-md-3">
							<Input
								type="text"
								placeholder="Search IPs..."
								value={filters.search}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('search', e.target.value)}
							/>
						</div>
						<div className="col-md-2">
							<Input
								type="date"
								placeholder="Start Date"
								value={filters.startDate}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('startDate', e.target.value)}
							/>
						</div>
						<div className="col-md-2">
							<Input
								type="date"
								placeholder="End Date"
								value={filters.endDate}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('endDate', e.target.value)}
							/>
						</div>
						<div className="col-md-2">
							<Select
								ariaLabel="Items per page"
								value={filters.per_page.toString()}
								onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('per_page', parseInt(e.target.value))}>
								<option value={10}>10</option>
								<option value={25}>25</option>
								<option value={50}>50</option>
								<option value={100}>100</option>
							</Select>
						</div>
						<div className="col-md-3 d-flex gap-2 justify-content-end">
							<Button color="primary" onClick={fetchData}>
								Filter
							</Button>
							<Button color="secondary" onClick={handleClearFilters}>
								Clear
							</Button>
						</div>
					</div>

					{/* Summary Cards */}
					<div className="row mb-4">
						<div className="col-md-3">
							<Card className="border-0 shadow-sm">
								<CardBody className="text-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
									<h3 className="text-white mb-2">{statistics.totalRequests}</h3>
									<p className="text-white-50 mb-0">Total Requests</p>
								</CardBody>
							</Card>
						</div>
						<div className="col-md-3">
							<Card className="border-0 shadow-sm">
								<CardBody className="text-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
									<h3 className="text-white mb-2">{statistics.uniqueIPs}</h3>
									<p className="text-white-50 mb-0">Unique IPs</p>
								</CardBody>
							</Card>
						</div>
						<div className="col-md-3">
							<Card className="border-0 shadow-sm">
								<CardBody className="text-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
									<h3 className="text-white mb-2">{statistics.avgRequestsPerIP.toFixed(1)}</h3>
									<p className="text-white-50 mb-0">Avg. Requests per IP</p>
								</CardBody>
							</Card>
						</div>
						<div className="col-md-3">
							<Card className="border-0 shadow-sm">
								<CardBody className="text-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
									<h3 className="text-white mb-2">{statistics.completionRate.toFixed(1)}%</h3>
									<p className="text-white-50 mb-0">Completion Rate</p>
								</CardBody>
							</Card>
						</div>
					</div>

					{/* Data Table */}
					{loading ? (
						<div className="text-center py-4">
							<Spinner color="primary" />
							<p>Loading data...</p>
						</div>
					) : (
						<table className="table table-modern">
							<thead>
								<tr>
									<th onClick={() => toggleSortOrder('ip')}>
										<div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
											IP Address
											{sortField === 'ip' && (
												sortOrder === 'asc' ? (
													<img
														src={ascendingIcon}
														alt="Ascending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												) : (
													<img
														src={descendingIcon}
														alt="Descending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												)
											)}
										</div>
									</th>
									<th onClick={() => toggleSortOrder('requestCount')}>
										<div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
											Request Count
											{sortField === 'requestCount' && (
												sortOrder === 'asc' ? (
													<img
														src={ascendingIcon}
														alt="Ascending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												) : (
													<img
														src={descendingIcon}
														alt="Descending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												)
											)}
										</div>
									</th>
									<th>Country</th>
									<th>Region</th>
									<th>City</th>
									<th onClick={() => toggleSortOrder('countryCode')}>
										<div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
											Country Code
											{sortField === 'countryCode' && (
												sortOrder === 'asc' ? (
													<img
														src={ascendingIcon}
														alt="Ascending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												) : (
													<img
														src={descendingIcon}
														alt="Descending"
														style={{
															marginLeft: '5px',
															verticalAlign: 'middle',
															cursor: 'pointer',
															width: '16px',
															height: '16px',
														}}
													/>
												)
											)}
										</div>
									</th>
								</tr>
							</thead>
							<tbody>
								{data.map((item, index) => (
									<tr key={item.ip}>
										<td>
											<span className="fw-bold">{item.ip}</span>
										</td>
										<td>
											<Badge color="primary">{item.requestCount}</Badge>
										</td>
										<td>
											<span className="fw-bold">{item.country || 'Unknown'}</span>
										</td>
										<td>{item.region || 'Unknown'}</td>
										<td>{item.city || 'Unknown'}</td>
										<td>
											<Badge color="info">{item.countryCode || 'N/A'}</Badge>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}

					{data.length === 0 && !loading && (
						<div className="text-center py-4">
							<p className="text-muted">No data available for the selected filters.</p>
						</div>
					)}

					<PaginationButtons
						label="entries"
						setCurrentPage={setCurrentPage}
						currentPage={currentPage}
						perPage={perPage}
						setPerPage={setPerPage}
						totalItems={pagination.total_records}
					/>
				</CardBody>
			</Card>
		</div>
	);
};

export default CountryReport;
